import React, { useCallback, useEffect, useMemo } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import _cloneDeep from 'lodash/cloneDeep';
import _includes from 'lodash/includes';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import {
  fetchRegionGroupsAndTasksByRegionId,
  removeSelectedRestrictionOption,
  setIsCancelModalOpen,
  setPageProgress,
  setSelectedRestrictionOption,
  setSelectedRestrictionOptions,
  setSelectedTasks,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeAllToastsForMessage, createToastIfNoneForMessage } from 'src/store/toastsSlice';
import { type IdentifyBodyLocationEnum, JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import {
  type AllRestrictionTypes,
  type FormattedTaskOption,
  getEffectiveShiftLength,
  initWeightRestrictionTaskGroupChildren,
  isRestrictionTaskGroup,
  isTimeRestrictionTask,
  isWeightRestrictionTaskGroup,
  isYesNoRestrictionTask,
  type RestrictionOption,
  selectedRestrictionOptionsToJmrTaskList,
  validateSelectedRestrictionOptions,
  validateWeightRestrictionTaskGroupChildren,
} from 'src/utils/restrictionUtils';
import { setupPreselectedRestrictionOptions } from 'src/utils/restrictionUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJmrPageTitle, NewJMRPageWrapper } from '../pageLayout/pageLayout';

import AllTasksSelector from './allTasksSelector';

import HoursSelector from './restrictionItemBox/hoursSelector';
import IdentifyBodyLocationField from './restrictionItemBox/identifyBodyLocationField';
import RestrictionItemBox from './restrictionItemBox/restrictionItemBox';
import TaskSelector from './restrictionItemBox/taskSelector';
import WeightRestrictionTaskGroupElements from './restrictionItemBox/weightRestrictionTaskGroup';

// **** Constants **** //

// Exporting for testing
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

// **** Types **** //

interface RestrictionItemBoxChildrenWrapperProps {
  index: number;
  isDisabled: boolean;
  restrictionItem: RestrictionOption;
  shiftLength: number;
}

// **** Functional Components **** //

/**
 * Main component.
 */
const RestrictionsPage = () => {
  const dispatch = useAppDispatch();
  const {
    selectedRestrictionOptions,
    restrictionOptionsList,
    isFetchingRegionGroupsAndTasksByRegionId,
    jmrFlowType,
    selectedTasks: selectedJmrTasks,
    backgroundFormData,
  } = useAppSelector((state) => state.newJmrPage);
  const shiftLength = getEffectiveShiftLength(backgroundFormData);
  const shiftLengthStr = String(shiftLength);
  const showSpinner = isFetchingRegionGroupsAndTasksByRegionId && !restrictionOptionsList.length;
  const isRestrictionsPageReadOnly = jmrFlowType === JmrFlowTypes.UpdateDates;

  // ------------------------------- onLoad -------------------------------- //

  // If updating a Placement, set the list of pre-selected items when the group of
  // selected jmr-tasks change
  useEffect(() => {
    const { CompleteTransfer, UpdateDates, AlternativePlacementRecommended } = JmrFlowTypes;
    const shouldReuseExistingRestrictions = _includes(
      [CompleteTransfer, UpdateDates, AlternativePlacementRecommended],
      jmrFlowType,
    );
    if (selectedJmrTasks.length > 0 && shouldReuseExistingRestrictions && !selectedRestrictionOptions.length) {
      const items = setupPreselectedRestrictionOptions(selectedJmrTasks, restrictionOptionsList, shiftLength);
      dispatch(setSelectedRestrictionOptions(items));
    }
  }, [selectedJmrTasks, restrictionOptionsList, shiftLength, selectedRestrictionOptions.length]);

  // Fetch tasks/groups based on region-id of the site.
  useEffect(() => {
    const regionId = backgroundFormData.site?.regionId;
    if (regionId !== undefined && !restrictionOptionsList.length) {
      dispatch(fetchRegionGroupsAndTasksByRegionId(regionId));
    }
  }, [backgroundFormData.site?.regionId, restrictionOptionsList.length]);

  // --------------------------- TaskSelector stuff ------------------------ //

  // So we don't have to hardcaste to truthy and dont' modify original options
  const getRestrictionOption = useCallback(
    (entityId: string) => {
      const map = new Map<string, RestrictionOption>(restrictionOptionsList.map((o) => [o.entityId, o]));
      const value = map.get(entityId);
      if (!value) throw new Error(`Entity Id ${entityId} not found in restrictionOptionsList`);
      return _cloneDeep(value);
    },
    [restrictionOptionsList],
  );

  // Track selected options
  const selectedOptionsMap = useMemo(
    () => new Map<string, RestrictionOption>(selectedRestrictionOptions.map((o) => [o.entityId, o])),
    [selectedRestrictionOptions],
  );

  // All dropdown selector options for Restrictions
  const allFormattedRestrictionTaskOptions: FormattedTaskOption[] = useMemo(
    () =>
      restrictionOptionsList.map((o) => ({
        label: o.label,
        value: o.entityId,
        key: o.entityId,
      })),
    [restrictionOptionsList],
  );

  // When the selection changes
  const onAllTasksSelectorChange = useCallback(
    (entityIds: string[]) => {
      const updatedList = entityIds.map((id) => {
        const selectedOpt = selectedOptionsMap.get(id);
        return !!selectedOpt ? selectedOpt : getRestrictionOption(id);
      });
      dispatch(setSelectedRestrictionOptions(updatedList));
    },
    [selectedRestrictionOptions, getRestrictionOption],
  );

  // When the selection changes
  const onIndividualRestrictionBoxTaskChange = useCallback(
    (oldEntityId: string, newEntityId: string) => {
      if (oldEntityId !== newEntityId) {
        const index = selectedRestrictionOptions.findIndex((o) => o.entityId === oldEntityId);
        const updatedArr = [...selectedRestrictionOptions];
        updatedArr[index] = getRestrictionOption(newEntityId);
        dispatch(setSelectedRestrictionOptions(updatedArr));
      }
    },
    [selectedRestrictionOptions, getRestrictionOption],
  );

  // The individual Restriction Item boxes need to select from the options
  // which aren't taken. But for the selector to behave properly the currently
  // selected item still needs to be in the list.
  const getCurrentAndUnselectedOptions = useCallback(
    (currentId: string) => {
      return allFormattedRestrictionTaskOptions.filter((item) => {
        return currentId === item.value || !selectedOptionsMap.has(item.value);
      });
    },
    [allFormattedRestrictionTaskOptions, selectedOptionsMap],
  );

  // --------------------------- Footer ------------------------------------ //

  // Generate job matches and navigate to the next page
  const goToJobMatchesPage = useCallback(() => {
    let isValid = true;
    if (!isRestrictionsPageReadOnly) {
      const result = validateSelectedRestrictionOptions(selectedRestrictionOptions);
      isValid = result.isValid;
      if (!isValid) {
        dispatch(setSelectedRestrictionOptions(result.updatedList));
      }
    }
    if (isValid) {
      const jmrTasks = selectedRestrictionOptionsToJmrTaskList(selectedRestrictionOptions, shiftLengthStr);
      if (!isRestrictionsPageReadOnly) dispatch(setSelectedTasks(jmrTasks));
      dispatch(closeAllToastsForMessage(TOAST_MESSAGE));
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    } else {
      dispatch(
        createToastIfNoneForMessage({
          type: 'error',
          message: TOAST_MESSAGE,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [selectedRestrictionOptions, isRestrictionsPageReadOnly, shiftLength]);

  // ------------------------------ DOM ------------------------------------ //

  return (
    <NewJMRPageWrapper>
      <LoadingSpinnerWrapper showSpinner={showSpinner}>
        <NewJMRPageMainBody xs="85%" md="75%" lg="60%">
          <span style={{ pointerEvents: isRestrictionsPageReadOnly ? 'none' : 'auto' }}>
            {/* Header */}
            <Column spacing="300" spacingInset="300">
              <NewJmrPageTitle
                title="Add and Edit Restriction(s)"
                subtitle="Please add and enter all restrictions as denoted within the associates RFI."
              />
              <Box spacingInset="200" />
              <Text>Restrictions*</Text>
              <AllTasksSelector
                availableOptions={allFormattedRestrictionTaskOptions}
                selectedOptions={selectedRestrictionOptions.map((item) => item.entityId)}
                onSelectTasks={onAllTasksSelectorChange}
                isDisabled={isRestrictionsPageReadOnly}
              />
            </Column>

            {/* Body (list of RestrictionItem's) */}
            <Column spacing="300" spacingInset="300">
              {selectedRestrictionOptions.map((item, index) => (
                <RestrictionItemBox
                  key={item.entityId}
                  restrictionNumber={index + 1}
                  onRemoveRestriction={() => dispatch(removeSelectedRestrictionOption(item.entityId))}
                  isDisabled={isRestrictionsPageReadOnly}
                >
                  {/* Task Selector */}
                  <Box spacingInset="none none 400 none">
                    <Text>Task*</Text>
                    <TaskSelector
                      availableOptions={getCurrentAndUnselectedOptions(item.entityId)}
                      onSelectTask={(entityId) => onIndividualRestrictionBoxTaskChange(item.entityId, entityId)}
                      selectedOption={item.entityId}
                      isDisabled={isRestrictionsPageReadOnly}
                      index={index}
                    />
                  </Box>

                  {/* Elements unique for different Restriction types  */}
                  <RestrictionItemBoxChildrenWrapper
                    key={item.entityId}
                    index={index}
                    restrictionItem={item}
                    isDisabled={isRestrictionsPageReadOnly}
                    shiftLength={shiftLength}
                  />
                </RestrictionItemBox>
              ))}
              <Box spacingInset="300" />
            </Column>
          </span>
        </NewJMRPageMainBody>
      </LoadingSpinnerWrapper>

      {/* Footer */}
      <NewJMRPageFooter leftSideContent={<Text>{selectedRestrictionOptions.length} Restriction(s)</Text>}>
        <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
          Cancel
        </Button>
        <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Background))}>
          Previous
        </Button>
        <Button data-cy="new-jmr__restrictions__next" onClick={goToJobMatchesPage}>
          Next
        </Button>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

/**
 * There's a lot of business logic for setting the RestrictionItem's children, so I created
 * a wrapper component for it. Nothing's repeat logic, every calculation is for an individual
 * restriction.
 */
const RestrictionItemBoxChildrenWrapper = ({
  isDisabled,
  index,
  restrictionItem,
  shiftLength,
}: RestrictionItemBoxChildrenWrapperProps) => {
  const dispatch = useAppDispatch();
  const updateRestrictionOption = (arg: AllRestrictionTypes) => dispatch(setSelectedRestrictionOption(arg));

  // Render body-location selector
  const identifyBodyLocationOrNull = useMemo(() => {
    const { identifyBodyLocation, bodyLocationValue } = restrictionItem;
    if (!(identifyBodyLocation || (isDisabled && !!bodyLocationValue))) {
      return null;
    }
    const onChange = (value: IdentifyBodyLocationEnum | null) => {
      const toUpdate = { ...restrictionItem, bodyLocationValue: value };
      if (isRestrictionTaskGroup(toUpdate)) {
        toUpdate.restrictionTasks = toUpdate.restrictionTasks.map((child) => ({
          ...child,
          bodyLocationValue: value,
        }));
      }
      updateRestrictionOption(toUpdate);
    };
    return (
      <IdentifyBodyLocationField
        spacingInset="none none 400 none"
        bodyLocationValue={restrictionItem.bodyLocationValue ?? null}
        onChangeBodyLocationValue={onChange}
        isDisabled={isDisabled}
      />
    );
  }, [restrictionItem, isDisabled]);

  // Render time-restriction task
  const timeRestrictionTaskOrNull = useMemo(() => {
    if (!isTimeRestrictionTask(restrictionItem)) return null;
    const onChange = (newValue: string) => {
      const newValueStr = String(newValue);
      if (newValueStr !== restrictionItem.restrictedValue) {
        const { error, ...toUpdate } = restrictionItem;
        toUpdate.restrictedValue = newValueStr;
        updateRestrictionOption(toUpdate);
      }
    };
    return (
      <HoursSelector
        selectedValue={restrictionItem.restrictedValue}
        onSelectValue={onChange}
        isDisabled={isDisabled}
        shiftLength={shiftLength}
        restrictionLabel={restrictionItem.label}
        errorMessage={restrictionItem.error}
        index={index}
      />
    );
  }, [restrictionItem, isDisabled, shiftLength, index]);

  // Yes/no tasks are always set the false
  const restrictionNotAllowTextOrNull = useMemo(() => {
    if (!isYesNoRestrictionTask(restrictionItem)) return null;
    return (
      <Box spacingInset="200">
        <Text color="secondary" data-testid="new-jmr__yes-no-restriction-field-label">
          0 hrs (Associate cannot perform <b>{restrictionItem?.label}</b>)
        </Text>
      </Box>
    );
  }, [restrictionItem]);

  // Handle <MaxRangeSelector /> updates
  const weightRestrictionGroupMaxRangeSelectorOrNull = useMemo(() => {
    if (!isWeightRestrictionTaskGroup(restrictionItem)) return null;
    const onChange = (value: number) => {
      const { error, ...toUpdate } = restrictionItem;
      toUpdate.maxWeightRange = value;
      if (restrictionItem.showChildSelectors && value === 0) {
        toUpdate.showChildSelectors = false;
      }
      toUpdate.restrictionTasks = initWeightRestrictionTaskGroupChildren(toUpdate, String(shiftLength));
      updateRestrictionOption(toUpdate);
    };
    return (
      <WeightRestrictionTaskGroupElements.MaxRangeSelector
        restrictionLabel={restrictionItem.label}
        restrictionMaxUnit={restrictionItem.restrictionMaxUnit}
        restrictionChildTasks={restrictionItem.restrictionTasks}
        selectedValue={restrictionItem.maxWeightRange}
        onSelectValue={onChange}
        isDisabled={isDisabled}
        index={index}
        spacingInset="none none 400 none"
        errorMessage={restrictionItem.error}
      />
    );
  }, [restrictionItem, isDisabled, index, shiftLength]);

  // Show the toggle (slider element) for displaying child weight selectors
  const weightRestrictionGroupSliderOrNull = useMemo(() => {
    if (
      !(
        isWeightRestrictionTaskGroup(restrictionItem) &&
        restrictionItem.maxWeightRange !== undefined &&
        restrictionItem.maxWeightRange > 0
      )
    ) {
      return null;
    }
    const onToggleValue = (value: boolean) => {
      const toUpdate = { ...restrictionItem, showChildSelectors: value };
      if (!value) {
        toUpdate.restrictionTasks = initWeightRestrictionTaskGroupChildren(toUpdate, String(shiftLength));
      }
      updateRestrictionOption(toUpdate);
    };
    return (
      <WeightRestrictionTaskGroupElements.ToggleShowChildSelectors
        restrictionLabel={restrictionItem.label}
        restrictionMaxUnit={restrictionItem.restrictionMaxUnit}
        maxWeightRange={restrictionItem.maxWeightRange}
        value={!!restrictionItem.showChildSelectors}
        onToggleValue={onToggleValue}
        isDisabled={isDisabled}
        index={index}
      />
    );
  }, [restrictionItem, isDisabled, index, shiftLength]);

  // Update parent when child is updated
  const weightRestrictionGroupChildSelectorsOrNull = useMemo(() => {
    if (!(isWeightRestrictionTaskGroup(restrictionItem) && restrictionItem.showChildSelectors)) {
      return null;
    }
    const onUpdateChildSelection = (index: number, value: number) => {
      const children = [...restrictionItem.restrictionTasks];
      children[index] = { ...children[index], restrictedValue: String(value) };
      let toUpdate = { ...restrictionItem, restrictionTasks: children };
      toUpdate = validateWeightRestrictionTaskGroupChildren(toUpdate);
      updateRestrictionOption(toUpdate);
    };
    return (
      <WeightRestrictionTaskGroupElements.ChildSelectors
        index={index}
        childSelection={restrictionItem.restrictionTasks}
        shiftLength={shiftLength}
        isDisabled={isDisabled}
        onUpdateChildSelection={onUpdateChildSelection}
      />
    );
  }, [restrictionItem, shiftLength, isDisabled, index]);

  // So far, only groups that are supported are
  const nonWeightMeasuredGroupOrNull = useMemo(() => {
    if (!(isRestrictionTaskGroup(restrictionItem) && !isWeightRestrictionTaskGroup(restrictionItem))) {
      return null;
    }
    return (
      <Text color="secondary">
        <i>
          Only weight measured task-groups can be configured at this time. This task will not be used for Job Match
          Results.
        </i>
      </Text>
    );
  }, [restrictionItem]);

  return (
    <>
      {identifyBodyLocationOrNull}
      {weightRestrictionGroupMaxRangeSelectorOrNull}
      {timeRestrictionTaskOrNull}
      {restrictionNotAllowTextOrNull}
      {weightRestrictionGroupSliderOrNull}
      {weightRestrictionGroupChildSelectorsOrNull}
      {nonWeightMeasuredGroupOrNull}
    </>
  );
};

// **** Export **** //

export default RestrictionsPage;
